/**
 * Core Monaco Editor Plugins
 *
 * Plugin system for extending Monaco Editor with AI features.
 */
import type { editor } from 'monaco-editor';

export interface EditorPlugin {
  name: string;
  description: string;
  activate: (monaco: typeof import('monaco-editor'), editor: editor.IStandaloneCodeEditor) => void;
  deactivate?: () => void;
}

export interface PluginManager {
  register: (plugin: EditorPlugin) => void;
  unregister: (name: string) => void;
  getPlugin: (name: string) => EditorPlugin | undefined;
  getAllPlugins: () => EditorPlugin[];
  activateAll: (monaco: typeof import('monaco-editor'), editor: editor.IStandaloneCodeEditor) => void;
  deactivateAll: () => void;
}

/**
 * Create a plugin manager instance.
 */
export function createPluginManager(): PluginManager {
  const plugins = new Map<string, EditorPlugin>();

  return {
    register(plugin: EditorPlugin) {
      plugins.set(plugin.name, plugin);
    },

    unregister(name: string) {
      const plugin = plugins.get(name);
      if (plugin?.deactivate) {
        plugin.deactivate();
      }
      plugins.delete(name);
    },

    getPlugin(name: string) {
      return plugins.get(name);
    },

    getAllPlugins() {
      return Array.from(plugins.values());
    },

    activateAll(monaco, editor) {
      for (const plugin of plugins.values()) {
        try {
          plugin.activate(monaco, editor);
        } catch (error) {
          console.error(`Failed to activate plugin ${plugin.name}:`, error);
        }
      }
    },

    deactivateAll() {
      for (const plugin of plugins.values()) {
        if (plugin.deactivate) {
          try {
            plugin.deactivate();
          } catch (error) {
            console.error(`Failed to deactivate plugin ${plugin.name}:`, error);
          }
        }
      }
    },
  };
}

// Global plugin manager
export const pluginManager = createPluginManager();

// Re-export plugins
export { aiCompletionPlugin } from './aiCompletionPlugin';
export { codeActionsPlugin } from './codeActionsPlugin';
export { vibeThemePlugin } from './vibeThemePlugin';

// Import plugins for default list
import { aiCompletionPlugin } from './aiCompletionPlugin';
import { codeActionsPlugin } from './codeActionsPlugin';
import { vibeThemePlugin } from './vibeThemePlugin';

// Default plugins to load
export const defaultPlugins: EditorPlugin[] = [
  vibeThemePlugin,
  aiCompletionPlugin,
  codeActionsPlugin,
];
